import React, { useState, useEffect } from 'react';
import {
  getSyncJobCostsCurrent,
  getSyncJobCostCurrentSummary,
  getSyncJobCostPhases,
  getSyncJobCostMonthlySummary,
  getSyncJobCostPeriods,
  getSyncJobCostsMonthly,
} from '../api';
import { useTheme } from '../hooks/useTheme';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  const num = parseFloat(value);
  const sign = num < 0 ? '-' : '';
  return sign + '$' + Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '0%';
  return parseFloat(value).toFixed(1) + '%';
};

const SummaryCard = ({ label, value, color, theme }) => (
  <div style={{
    flex: '1 1 200px', padding: '20px 24px',
    backgroundColor: theme.bg, border: `1px solid ${theme.cardBorder}`,
    borderRadius: 12, minWidth: 180,
  }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
      {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: color || theme.heading, letterSpacing: '-0.5px' }}>
      {value}
    </div>
  </div>
);

const JobCostTab = ({ projectId, project }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [phases, setPhases] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [phaseDetails, setPhaseDetails] = useState({});

  // Try to derive CE job_id from project — check project_number or ce_job_id field
  const ceJobId = project?.ce_job_id || project?.project_number || null;

  useEffect(() => {
    if (!ceJobId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [summaryData, phaseData, trendData] = await Promise.all([
          getSyncJobCostCurrentSummary({ job_id: ceJobId }),
          getSyncJobCostPhases({ job_id: ceJobId, source: 'current' }),
          getSyncJobCostMonthlySummary(ceJobId),
        ]);

        setSummary(summaryData?.[0] || null);
        setPhases(phaseData || []);
        setMonthlyTrend(trendData || []);
      } catch (err) {
        console.error('Failed to load job cost data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ceJobId]);

  const handlePhaseClick = async (phase) => {
    if (expandedPhase === phase.phase) {
      setExpandedPhase(null);
      return;
    }

    // Fetch line items for this phase if not cached
    if (!phaseDetails[phase.phase]) {
      try {
        const details = await getSyncJobCostsCurrent(ceJobId);
        const filtered = details.filter(r => r.phase === phase.phase);
        setPhaseDetails(prev => ({ ...prev, [phase.phase]: filtered }));
      } catch (err) {
        console.error('Failed to load phase details:', err);
      }
    }
    setExpandedPhase(phase.phase);
  };

  const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>
        Loading cost data...
      </div>
    );
  }

  if (!ceJobId) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No CE Job ID Linked</div>
        <div style={{ fontSize: 13 }}>
          Set the project number to match the ComputerEase job ID (e.g., CHICO-D-02) to view cost data.
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>
        Failed to load cost data: {error}
      </div>
    );
  }

  if (!summary) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No Cost Data Available</div>
        <div style={{ fontSize: 13 }}>
          No synced cost data found for job {ceJobId}. Run a CE Agent pull to populate data.
        </div>
      </div>
    );
  }

  const overrun = parseFloat(summary.total_overrun || 0);
  const overrunColor = overrun > 0 ? '#dc2626' : '#16a34a';

  // Chart calculations
  const maxCost = Math.max(...monthlyTrend.map(m => parseFloat(m.total_cost || 0)), 1);
  const chartHeight = 180;

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <SummaryCard label="Total Budget" value={formatCurrency(summary.total_budget)} theme={theme} />
        <SummaryCard label="Actual Cost" value={formatCurrency(summary.total_cost)} theme={theme} />
        <SummaryCard label="Committed (POs)" value={formatCurrency(summary.total_committed)} theme={theme} />
        <SummaryCard label="Over / Under" value={formatCurrency(summary.total_overrun)} color={overrunColor} theme={theme} />
        <SummaryCard label="% Spent" value={formatPercent(summary.percent_spent)} theme={theme} />
      </div>

      {/* Monthly Trend */}
      {monthlyTrend.length > 1 && (
        <div style={{
          backgroundColor: theme.bg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: 12, padding: 24, marginBottom: 24,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.heading, marginBottom: 16 }}>
            Monthly Cost Trend
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: chartHeight }}>
            {monthlyTrend.map((m, i) => {
              const cost = parseFloat(m.total_cost || 0);
              const barHeight = Math.max((cost / maxCost) * (chartHeight - 30), 2);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    fontSize: 9, color: theme.textMuted, marginBottom: 4, whiteSpace: 'nowrap',
                  }}>
                    {cost > 0 ? formatCurrency(cost) : ''}
                  </div>
                  <div style={{
                    width: '100%', maxWidth: 40, height: barHeight,
                    backgroundColor: '#6366f1', borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                  }} />
                  <div style={{
                    fontSize: 10, color: theme.textMuted, marginTop: 6, whiteSpace: 'nowrap',
                  }}>
                    {monthNames[m.period_month]} {String(m.period_year).slice(-2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Phase Breakdown */}
      <div style={{
        backgroundColor: theme.bg, border: `1px solid ${theme.cardBorder}`,
        borderRadius: 12, overflow: 'hidden',
      }}>
        <div style={{ padding: '16px 24px', borderBottom: `1px solid ${theme.tableBorder}` }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: theme.heading }}>
            Phase Breakdown
          </span>
          <span style={{ fontSize: 12, color: theme.textMuted, marginLeft: 8 }}>
            ({phases.length} phases)
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: theme.tableHeaderBg }}>
              <th style={thStyle(theme)}>Phase</th>
              <th style={thStyle(theme)}>Name</th>
              <th style={{ ...thStyle(theme), textAlign: 'right' }}>Budget</th>
              <th style={{ ...thStyle(theme), textAlign: 'right' }}>Cost</th>
              <th style={{ ...thStyle(theme), textAlign: 'right' }}>Committed</th>
              <th style={{ ...thStyle(theme), textAlign: 'right' }}>Over/Under</th>
              <th style={{ ...thStyle(theme), textAlign: 'right' }}>% Spent</th>
            </tr>
          </thead>
          <tbody>
            {phases.map((phase, i) => {
              const phaseOverrun = parseFloat(phase.total_overrun || 0);
              const isExpanded = expandedPhase === phase.phase;
              return (
                <React.Fragment key={phase.phase || i}>
                  <tr
                    onClick={() => handlePhaseClick(phase)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isExpanded ? theme.tableRowHover : 'transparent',
                      borderBottom: `1px solid ${theme.tableBorder}`,
                    }}
                  >
                    <td style={tdStyle(theme)}>
                      <span style={{ marginRight: 6, fontSize: 10, color: theme.textMuted }}>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                      {phase.phase}
                    </td>
                    <td style={tdStyle(theme)}>{phase.phase_name || '–'}</td>
                    <td style={{ ...tdStyle(theme), textAlign: 'right' }}>{formatCurrency(phase.total_budget)}</td>
                    <td style={{ ...tdStyle(theme), textAlign: 'right' }}>{formatCurrency(phase.total_cost)}</td>
                    <td style={{ ...tdStyle(theme), textAlign: 'right' }}>{formatCurrency(phase.total_committed)}</td>
                    <td style={{ ...tdStyle(theme), textAlign: 'right', color: phaseOverrun > 0 ? '#dc2626' : '#16a34a' }}>
                      {formatCurrency(phase.total_overrun)}
                    </td>
                    <td style={{ ...tdStyle(theme), textAlign: 'right' }}>{formatPercent(phase.percent_spent)}</td>
                  </tr>
                  {/* Expanded category rows */}
                  {isExpanded && phaseDetails[phase.phase] && phaseDetails[phase.phase].map((cat, j) => (
                    <tr key={`${phase.phase}-${j}`} style={{
                      backgroundColor: theme.bgAlt,
                      borderBottom: `1px solid ${theme.tableBorder}`,
                    }}>
                      <td style={{ ...tdStyle(theme), paddingLeft: 36, fontSize: 12 }}>{cat.category}</td>
                      <td style={{ ...tdStyle(theme), fontSize: 12 }}>{cat.category_name || '–'}</td>
                      <td style={{ ...tdStyle(theme), textAlign: 'right', fontSize: 12 }}>{formatCurrency(cat.cost_budget)}</td>
                      <td style={{ ...tdStyle(theme), textAlign: 'right', fontSize: 12 }}>{formatCurrency(cat.cost)}</td>
                      <td style={{ ...tdStyle(theme), textAlign: 'right', fontSize: 12 }}>{formatCurrency(cat.cost_committed)}</td>
                      <td style={{ ...tdStyle(theme), textAlign: 'right', fontSize: 12, color: parseFloat(cat.cost_overrun || 0) > 0 ? '#dc2626' : '#16a34a' }}>
                        {formatCurrency(cat.cost_overrun)}
                      </td>
                      <td style={{ ...tdStyle(theme), textAlign: 'right', fontSize: 12 }}>
                        {parseFloat(cat.cost_budget || 0) > 0
                          ? formatPercent(100 * parseFloat(cat.cost || 0) / parseFloat(cat.cost_budget))
                          : '–'}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {/* Totals row */}
            {phases.length > 0 && (
              <tr style={{ backgroundColor: theme.totalsBg, fontWeight: 700, borderTop: `2px solid ${theme.tableBorder}` }}>
                <td style={tdStyle(theme)} colSpan={2}>Total</td>
                <td style={{ ...tdStyle(theme), textAlign: 'right' }}>{formatCurrency(summary.total_budget)}</td>
                <td style={{ ...tdStyle(theme), textAlign: 'right' }}>{formatCurrency(summary.total_cost)}</td>
                <td style={{ ...tdStyle(theme), textAlign: 'right' }}>{formatCurrency(summary.total_committed)}</td>
                <td style={{ ...tdStyle(theme), textAlign: 'right', color: overrunColor }}>{formatCurrency(summary.total_overrun)}</td>
                <td style={{ ...tdStyle(theme), textAlign: 'right' }}>{formatPercent(summary.percent_spent)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Data freshness */}
      {summary.report_date && (
        <div style={{ marginTop: 12, fontSize: 11, color: theme.textMuted, textAlign: 'right' }}>
          Data as of {new Date(summary.report_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          {summary.period_to && ` · Through ${summary.period_to}`}
        </div>
      )}
    </div>
  );
};

const thStyle = (theme) => ({
  padding: '10px 16px', fontSize: 11, fontWeight: 600, color: theme.tableHeaderColor,
  textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left',
  borderBottom: `1px solid ${theme.tableBorder}`,
});

const tdStyle = (theme) => ({
  padding: '10px 16px', fontSize: 13, color: theme.text,
});

export default JobCostTab;
